import { defineStore } from 'pinia'
import type { RouteLocation } from 'vue-router'
import store from '@/stores/index'
import { supabase } from '@/supabase'
import httpClient from '@/api/http-client'
import { isLocalDevAuthBypass } from '@/config/runtimeEnv'

const LOCAL_DEV_USER = {
    id: 'local-dev',
    email: 'dev@joinlighthouse.ai',
    user_metadata: { email: 'dev@joinlighthouse.ai' },
}

const STAFF_EMAIL_SUFFIX = '@joinlighthouse.ai'
const VALIDATE_TIMEOUT_MS = 10_000

let inFlightLoadUser: Promise<void> | null = null

function emailLooksLikeStaff(email: unknown): boolean {
    return typeof email === 'string' && email.trim().toLowerCase().endsWith(STAFF_EMAIL_SUFFIX)
}

function inferIsGuest(user: any, persistedGuest: unknown): boolean {
    if (typeof persistedGuest === 'boolean') return persistedGuest
    if (!user) return false
    const email = user.email ?? user.user_metadata?.email
    return !emailLooksLikeStaff(email)
}

interface State {
    currentUser: any
    redirectRoute: Partial<RouteLocation> | null
    domainError: boolean
    isGuest: boolean
    accessResolved: boolean
}

interface Getters {
    [key: string]: (...args: any[]) => any
    isAuthenticated: () => boolean
    userEmail: () => string
    isGuestUser: () => boolean
    canAccessStaff: () => boolean
}

interface Actions {
    loadUser: () => Promise<void>
    _loadUser: () => Promise<void>
    _applyRestrictedSession: (user: any) => void
    clearUser: () => void
    saveRedirectRoute: (route: Partial<RouteLocation>) => void
    loadRedirectRoute: () => void
    clearRedirectRoute: () => void
    signInWithGoogle: () => Promise<{ error: any }>
    signOut: () => Promise<void>
}

function applyLocalDevStaffSession(target: { currentUser: any; isGuest: boolean; accessResolved: boolean; domainError: boolean }) {
    target.currentUser = LOCAL_DEV_USER
    target.isGuest = false
    target.accessResolved = true
    target.domainError = false
}

export const useAuthStore = defineStore<'auth', State, Getters, Actions>('auth', {
    state() {
        if (isLocalDevAuthBypass()) {
            return {
                currentUser: LOCAL_DEV_USER,
                redirectRoute: null,
                domainError: false,
                isGuest: false,
                accessResolved: true,
            }
        }
        const user = store.state?.user || null
        return {
            currentUser: user,
            redirectRoute: null,
            domainError: false,
            // Fail closed: persisted non-staff emails stay guests across refresh
            // until /validate explicitly confirms otherwise.
            isGuest: inferIsGuest(user, store.state?.isGuest),
            accessResolved: false,
        }
    },
    getters: {
        isAuthenticated() {
            return !!this.currentUser
        },
        userEmail() {
            return this.currentUser?.email ?? ''
        },
        isGuestUser() {
            if (this.isAuthenticated && !this.accessResolved) return true
            return !!this.isGuest
        },
        canAccessStaff() {
            return this.accessResolved && this.isAuthenticated && !this.isGuest
        },
    },
    actions: {
        async loadUser() {
            if (inFlightLoadUser) return inFlightLoadUser
            inFlightLoadUser = this._loadUser().finally(() => {
                inFlightLoadUser = null
            })
            return inFlightLoadUser
        },
        async _loadUser() {
            if (isLocalDevAuthBypass()) {
                applyLocalDevStaffSession(this)
                return
            }

            const session = (await supabase.auth.getSession())?.data?.session
            const user = session?.user
            if (!user) {
                this.clearUser()
                return
            }

            let response: { status?: number; data?: { is_guest?: boolean } } | undefined
            try {
                response = await httpClient.get('/internal/lightbase/validate', {
                    timeout: VALIDATE_TIMEOUT_MS,
                })
            } catch {
                this._applyRestrictedSession(user)
                return
            }

            const status = response?.status ?? 0

            if (status === 401 || status === 403) {
                await supabase.auth.signOut()
                this.clearUser()
                this.domainError = true
                return
            }

            // Network / 5xx / missing payload: keep the session but never grant staff.
            if (status !== 200 || typeof response?.data?.is_guest !== 'boolean') {
                this._applyRestrictedSession(user)
                return
            }

            this.currentUser = user
            this.isGuest = response.data.is_guest
            this.accessResolved = true
            this.domainError = false
            store.setUser(user, this.isGuest)
        },
        _applyRestrictedSession(user: any) {
            this.currentUser = user
            this.isGuest = true
            this.accessResolved = true
            this.domainError = false
            store.setUser(user, true)
        },
        clearUser() {
            if (isLocalDevAuthBypass()) {
                applyLocalDevStaffSession(this)
                return
            }
            store.setUser(null, false)
            this.currentUser = null
            this.isGuest = false
            this.accessResolved = true
        },
        saveRedirectRoute(route: Partial<RouteLocation>) {
            const { name, params, query, hash } = route

            localStorage.setItem(
                'redirectRoute',
                JSON.stringify({
                    name,
                    params,
                    query,
                    hash,
                }),
            )
        },
        loadRedirectRoute() {
            const route = JSON.parse(localStorage.getItem('redirectRoute') || 'null') as Partial<RouteLocation> | null
            this.redirectRoute = route
        },
        clearRedirectRoute() {
            localStorage.removeItem('redirectRoute')
            this.redirectRoute = null
        },
        async signInWithGoogle() {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { redirectTo: window.location.origin },
            })
            return { error }
        },
        async signOut() {
            if (isLocalDevAuthBypass()) return
            try {
                const { error } = await supabase.auth.signOut()
                this.clearUser()
                this.domainError = false
                store.clearLocalStorage()
                if (error) throw error
            } catch (error) {
                if (error instanceof Error) {
                    console.error(error.message)
                }
            }
        },
    },
})
